/* Celeste custom assets: enhance all Easify Size fields using inch units. */
(() => {
  if (window.__celesteEasifySize?.version === 34) return;
  window.__celesteEasifySize = { version: 34 };
  const wheelRowHeight = 28;
  const wheelPixelsPerRow = 120;
  const metadataCache = new WeakMap();
  const conditionalSizes = new WeakMap();
  let pickerText = null;
  try { pickerText = JSON.parse(document.getElementById?.('celeste-size-picker-text')?.textContent || 'null'); }
  catch { /* Leave the existing inputs usable when the optional configuration is unavailable. */ }
  let activeWheel = null;
  const states = new Map();
  let pendingFrame = 0;
  let observed = new Set();
  let pointerActive = false;
  let openFraction = null;
  let menuSerial = 0;
  const resizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(queueSync) : null;

  function queueSync() {
    if (pendingFrame) return;
    pendingFrame = requestAnimationFrame(sync);
  }

  function inchFields(option) {
    const dimension = option.querySelector('.po-dimension-content');
    if (!dimension) return [];
    return Array.from(dimension.children).filter(field => {
      if (!field.classList.contains('po-dimension-input') || !field.querySelector('input')) return false;
      const suffix = field.querySelector('.po-dimension-suffixField')?.textContent.trim();
      if (suffix) return /^(?:in\.?|inch(?:es)?|["″]|['‘’]{2})$/i.test(suffix);
      const label = field.querySelector('label')?.textContent || '';
      return /\binch(?:es)?\b|\(\s*in\.?\s*\)|["″]\s*$/i.test(label);
    });
  }

  function finiteBound(value) {
    if (typeof value !== 'number' && typeof value !== 'string') return NaN;
    if (typeof value === 'string' && !value.trim()) return NaN;
    const number = Number(value);
    return Number.isFinite(number) ? number : NaN;
  }

  function dimensionDefinition(option) {
    // Easify clones may share an option ID across sets. Resolve both DOM IDs
    // against the public storefront configuration, never labels or help text.
    const setId = option.closest('[data-option-set-id]')?.dataset.optionSetId;
    const optionId = option.dataset.optionId;
    const sets = window.TPOConfigs?.option_sets_searchable;
    if (!setId || !optionId || !Array.isArray(sets)) return null;
    const matchingSets = sets.filter(set => String(set?.handle) === setId);
    if (matchingSets.length !== 1) return null;
    const data = matchingSets[0].data;
    if (!data || !Array.isArray(data.options)) return null;
    const matches = data.options.filter(item => item?.type === 'dimension' && String(item.id) === optionId);
    if (matches.length !== 1) return null;
    const item = matches[0];
    const raw = item.metadata;
    const cached = metadataCache.get(item);
    if (cached && cached.raw === raw) return { item, data, metadata: cached.value };
    let value;
    try { value = typeof raw === 'string' ? JSON.parse(raw) : raw; }
    catch { return null; }
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    metadataCache.set(item, { raw, value });
    return { item, data, metadata: value };
  }

  function dimensionMetadata(option) {
    return dimensionDefinition(option)?.metadata || null;
  }

  function configuredBounds(option, fields) {
    const result = { min: [], max: [] };
    const metadata = dimensionMetadata(option);
    const dimension = option.querySelector('.po-dimension-content');
    const allFields = Array.from(dimension?.children || []).filter(field =>
      field.classList.contains('po-dimension-input') && field.querySelector('input'));
    // Match Easify's X/Y/Z render condition and original order, including metric
    // fields. A missing Y must not cause Z to inherit Y's bounds.
    const axes = metadata ? ['X', 'Y', 'Z'].filter(axis =>
      metadata[`label${axis}`] || metadata[`value${axis}`] || metadata[`suffix${axis}`]) : [];
    fields.forEach((field, index) => {
      const axis = axes.length === allFields.length ? axes[allFields.indexOf(field)] : null;
      result.min[index] = axis ? finiteBound(metadata[`min${axis}`]) : NaN;
      result.max[index] = axis ? finiteBound(metadata[`max${axis}`]) : NaN;
    });
    return result;
  }

  function conditionalSizeDescriptor(item, data, metadata) {
    let condition;
    try { condition = JSON.parse(item.conditions_serialized); }
    catch { return null; }
    if (!condition?.conditionalField || condition.action !== 'SHOW'
      || typeof item.title !== 'string' || !item.title.trim()) return null;
    const positive = condition.match === 'ANY';
    if (!positive && condition.match !== 'ALL') return null;
    const rules = condition.conditionOptions;
    if (!Array.isArray(rules) || !rules.length || rules.some(rule =>
      !rule || typeof rule.id !== 'string' || !rule.id || typeof rule.value !== 'string' || !rule.value
      || rule.relative !== (positive ? 'EQUAL' : 'NOT_EQUAL'))) return null;
    const reference = rules[0].id;
    if (rules.some(rule => rule.id !== reference)) return null;
    const sources = data.options.filter(option => option && `${option.id},${option.type}` === reference);
    // Only infer a complementary pair for a single-choice controller.
    if (sources.length !== 1 || !['image-swatches', 'radio-buttons', 'dropdown'].includes(sources[0].type)
      || ![undefined, null, false, 0, '0'].includes(sources[0].multi_select)) return null;
    const axes = ['X', 'Y', 'Z'].filter(axis =>
      metadata[`label${axis}`] || metadata[`value${axis}`] || metadata[`suffix${axis}`]);
    if (!axes.length || axes.some(axis => !/^(?:in\.?|inch(?:es)?)$/i.test(metadata[`suffix${axis}`] || '')
      || !Number.isFinite(finiteBound(metadata[`min${axis}`]))
      || !Number.isFinite(finiteBound(metadata[`max${axis}`])))) return null;
    return {
      positive, count: axes.length,
      key: JSON.stringify([item.title.trim(), reference, [...new Set(rules.map(rule => rule.value))].sort(),
        axes.map(axis => [axis, metadata[`label${axis}`] || '',
          finiteBound(metadata[`min${axis}`]), finiteBound(metadata[`max${axis}`])])])
    };
  }

  function conditionalSizeMemory(state) {
    const definition = dimensionDefinition(state.option);
    if (!definition) return null;
    const { item, data, metadata } = definition;
    const descriptor = conditionalSizeDescriptor(item, data, metadata);
    if (!descriptor || descriptor.count !== state.inputs.length) return null;
    const peers = data.options.filter(candidate => {
      if (candidate?.type !== 'dimension') return false;
      let config;
      try { config = typeof candidate.metadata === 'string' ? JSON.parse(candidate.metadata) : candidate.metadata; }
      catch { return false; }
      return config && conditionalSizeDescriptor(candidate, data, config)?.key === descriptor.key;
    });
    if (peers.length !== 2) return null;
    const peer = peers.find(candidate => candidate !== item);
    let peerMetadata;
    try { peerMetadata = typeof peer.metadata === 'string' ? JSON.parse(peer.metadata) : peer.metadata; }
    catch { return null; }
    if (conditionalSizeDescriptor(peer, data, peerMetadata)?.positive === descriptor.positive) return null;
    const scope = state.option.closest('[data-option-set-id]');
    let memories = conditionalSizes.get(scope);
    if (!memories) { memories = new Map(); conditionalSizes.set(scope, memories); }
    const key = JSON.stringify([scope.dataset.optionSetId, descriptor.key, peers.map(peer => peer.id).sort()]);
    if (!memories.has(key)) memories.set(key, { values: null, inputs: [], restoring: false });
    return memories.get(key);
  }

  function rememberConditionalSize(state, allowEmpty = false) {
    const memory = state.sizeMemory;
    if (!memory || memory.restoring || state.inputs.some((input, index) => input !== memory.inputs[index])) return;
    const values = state.inputs.map(input => input.value);
    if (!values.every((value, index) => {
      if (value === '') return allowEmpty;
      const number = finiteBound(value);
      const { min, max } = dimensionLimits(state, state.inputs[index]);
      return Number.isFinite(number) && number > 0 && number >= min && number <= max;
    })) { if (allowEmpty) memory.values = null; return; }
    memory.values = values;
  }

  function syncConditionalSizes() {
    const groups = new Map();
    states.forEach(state => {
      state.sizeMemory = conditionalSizeMemory(state);
      if (!state.sizeMemory) return;
      const members = groups.get(state.sizeMemory) || [];
      members.push(state);
      groups.set(state.sizeMemory, members);
    });
    groups.forEach((members, memory) => {
      // Ambiguous simultaneous fields must remain independent.
      if (members.length !== 1) { members.forEach(state => { state.sizeMemory = null; }); return; }
      const state = members[0];
      if (state.inputs.some(input => input.disabled || input.readOnly)) return;
      const replaced = state.inputs.length !== memory.inputs.length
        || state.inputs.some((input, index) => input !== memory.inputs[index]);
      memory.inputs = [...state.inputs];
      if (replaced && memory.values) {
        memory.restoring = true;
        try {
          state.inputs.forEach((input, index) => {
            const value = memory.values[index];
            const { min, max } = dimensionLimits(state, input);
            if (!input.isConnected || (value !== '' && (Number(value) < min || Number(value) > max))) return;
            Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set.call(input, value);
            // Restore the native decimal, allowing the newly visible option's own formula to run.
            input.dispatchEvent(new Event('input', { bubbles: true }));
            if (input.isConnected) input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        } finally { memory.restoring = false; }
      }
      rememberConditionalSize(state);
    });
  }

  function fractionalInches(decimal) {
    if (decimal === '') return '';
    const value = Number(decimal);
    if (!Number.isFinite(value) || value < 0) return decimal;
    const ticks = Math.round(value * 16);
    // Never round a customer's measurement to a different manufacturing size.
    if (Math.abs(value - ticks / 16) > 1e-10) return decimal;
    const whole = Math.floor(ticks / 16);
    let numerator = ticks % 16;
    let denominator = 16;
    if (!numerator) return String(whole);
    while (numerator % 2 === 0) { numerator /= 2; denominator /= 2; }
    return `${whole ? `${whole} ` : ''}${numerator}/${denominator}`;
  }

  function fractionRecord(target) {
    for (const state of states.values()) {
      for (const record of state.fractionFields.values()) {
        if (record.proxy === target || record.select === target) return { state, record };
      }
    }
  }

  function restoreFraction(record) {
    if (activeWheel?.record === record) closeSizeWheel(false);
    if (openFraction?.record === record) closeFractionMenu();
    clearTimeout(record.noticeTimer);
    record.menu?.remove();
    record.feedback?.remove();
    record.input.removeEventListener('invalid', record.onInvalid);
    record.proxy.remove();
    record.fractionShell?.remove();
    record.wholeUnit?.remove();
    if (record.addedSplitClass) record.root.classList.remove('inch-split-root');
    if (record.oldStyle === null) record.input.removeAttribute('style');
    else record.input.setAttribute('style', record.oldStyle);
    if (record.oldAriaHidden === null) record.input.removeAttribute('aria-hidden');
    else record.input.setAttribute('aria-hidden', record.oldAriaHidden);
    if (record.oldTabIndex === null) record.input.removeAttribute('tabindex');
    else record.input.setAttribute('tabindex', record.oldTabIndex);
    if (record.suffix) {
      record.suffix.textContent = record.oldSuffix;
      if (record.oldSuffixStyle === null) record.suffix.removeAttribute('style');
      else record.suffix.setAttribute('style', record.oldSuffixStyle);
    }
  }

  function dimensionLimits(state, input) {
    const index = state.inputs.indexOf(input);
    const ownMin = finiteBound(input.getAttribute('min'));
    const ownMax = finiteBound(input.getAttribute('max'));
    return {
      min: Number.isFinite(ownMin) ? ownMin : state.limits.min[index],
      max: Number.isFinite(ownMax) ? ownMax : state.limits.max[index]
    };
  }

  function hydrateFraction(record, decimal) {
    if (decimal === '') {
      record.proxy.value = '';
      record.select.value = '0';
      record.proxy.setCustomValidity('');
      record.lastWhole = '';
      record.incompatibleDecimal = null;
      refreshFractionMenu(record);
      return;
    }
    const value = Number(decimal);
    const ticks = Math.round(value * 16);
    const exact = Number.isFinite(value) && value >= 0 && Math.abs(value - ticks / 16) <= 1e-10;
    record.proxy.value = exact ? String(Math.floor(ticks / 16)) : '';
    record.lastWhole = record.proxy.value;
    record.incompatibleDecimal = exact ? null : decimal;
    record.select.value = exact ? String(ticks % 16) : '0';
    record.proxy.setCustomValidity(exact ? '' : 'This saved size is not a multiple of 1/16 inch. Choose whole inches and a fraction.');
    record.proxy.setAttribute('aria-invalid', String(!exact));
    if (!exact) showIntegerNotice(record, `Saved size ${decimal} in needs a whole number and a 1/16 fraction.`, true);
    refreshFractionMenu(record);
  }

  function updateFractionOptions(state, record) {
    if (!record.select) return;
    const { min, max } = dimensionLimits(state, record.input);
    const raw = record.proxy.value.trim();
    const whole = /^\d+$/.test(raw) ? Number(raw) : NaN;
    Array.from(record.select.options).forEach(option => {
      const value = whole + Number(option.value) / 16;
      const disabled = Number.isFinite(whole)
        && ((Number.isFinite(min) && value < min) || (Number.isFinite(max) && value > max));
      if (option.disabled !== disabled) option.disabled = disabled;
    });
    refreshFractionMenu(record);
  }

  function syncFractionFields(state) {
    state.fractionFields.forEach((record, input) => {
      if (state.inputs.includes(input) && record.proxy.isConnected && record.select.isConnected) return;
      restoreFraction(record);
      state.fractionFields.delete(input);
    });
    state.inputs.forEach((input, index) => {
      let record = state.fractionFields.get(input);
      if (!record) {
        const proxy = input.cloneNode(false);
        proxy.removeAttribute('id');
        proxy.removeAttribute('name');
        proxy.removeAttribute('aria-hidden');
        proxy.removeAttribute('tabindex');
        proxy.type = 'text';
        proxy.inputMode = 'numeric';
        proxy.autocomplete = 'off';
        proxy.spellcheck = false;
        proxy.dataset.inchFractionProxy = 'true';
        const label = input.closest('.po-dimension-input')?.querySelector('label')?.textContent.trim()
          || input.getAttribute('aria-label')?.trim()
          || (state.inputs.length === 1 ? state.option.querySelector('label')?.textContent.replace(/\*/g, '').trim()
            || /\b(width|length|depth|height|diameter)\b/i.exec(state.option.textContent)?.[0] : '')
          || (index === 0 ? 'Width' : index === 1 ? 'Length' : 'Size');
        proxy.setAttribute('aria-label', `${label}: whole inches`);
        const suffix = input.parentElement?.querySelector('.po-dimension-suffixField');
        record = {
          input, proxy, label, lastDecimal: input.value, root: input.parentElement,
          oldStyle: input.getAttribute('style'),
          oldAriaHidden: input.getAttribute('aria-hidden'),
          oldTabIndex: input.getAttribute('tabindex'),
          suffix, oldSuffix: suffix?.textContent, oldSuffixStyle: suffix?.getAttribute('style')
        };
        proxy.classList.add('inch-split-whole');
        proxy.setAttribute('pattern', '[0-9]*');
        proxy.setAttribute('placeholder', '0');
        record.addedSplitClass = !record.root.classList.contains('inch-split-root');
        record.root.classList.add('inch-split-root');
        const unit = document.createElement('span');
        unit.className = 'inch-split-whole-unit';
        unit.textContent = 'in';
        unit.setAttribute('aria-hidden', 'true');
        const shell = document.createElement('span');
        shell.className = 'inch-split-fraction';
        const select = document.createElement('select');
        select.className = 'inch-split-select';
        select.setAttribute('aria-label', `${label}: fractional inches`);
        select.dataset.inchFractionSelect = 'true';
        select.setAttribute('aria-hidden', 'true');
        select.setAttribute('tabindex', '-1');
        select.style.setProperty('display', 'none', 'important');
        for (let tick = 0; tick < 16; tick += 1) {
          const option = document.createElement('option');
          option.value = String(tick);
          option.textContent = `${fractionalInches(String(tick / 16))} in`;
          select.append(option);
        }
        shell.append(select);
        record.root.append(unit);
        record.root.append(shell);
        Object.assign(record, { select, fractionShell: shell, wholeUnit: unit });
        buildFractionMenu(state, record, label);
        if (suffix) suffix.style.setProperty('display', 'none', 'important');
        record.onInvalid = event => {
          event.preventDefault();
          if (wheelBounds(state, record)) { openSizeWheel(state, record, proxy); return; }
          const editor = proxy.checkValidity() ? record.trigger || proxy : proxy;
          editor.focus({ preventScroll: true });
          editor.reportValidity?.();
        };
        input.addEventListener('invalid', record.onInvalid);
        hydrateFraction(record, input.value);
        input.style.setProperty('display', 'none', 'important');
        input.setAttribute('aria-hidden', 'true');
        input.setAttribute('tabindex', '-1');
        input.after(proxy);
        state.fractionFields.set(input, record);
      }
      // Avoid rewriting reflected attributes: they are watched by MutationObserver.
      const wheel = wheelBounds(state, record);
      record.wheelEnabled = Boolean(wheel);
      const maximum = dimensionLimits(state, input).max;
      if (Number.isFinite(maximum) && maximum >= 1) {
        const maxLength = String(Math.floor(maximum)).length;
        if (record.proxy.maxLength !== maxLength) record.proxy.maxLength = maxLength;
      } else if (record.proxy.hasAttribute('maxlength')) record.proxy.removeAttribute('maxlength');
      for (const property of ['disabled', 'readOnly', 'required']) {
        const value = Boolean(input[property] || (property === 'readOnly' && wheel));
        if (record.proxy[property] !== value) record.proxy[property] = value;
      }
      record.proxy.setAttribute('aria-haspopup', wheel ? 'dialog' : 'false');
      record.trigger.setAttribute('aria-haspopup', wheel ? 'dialog' : 'listbox');
      record.trigger.setAttribute('role', wheel ? 'button' : 'combobox');
      record.trigger.setAttribute('aria-label', wheel ? record.label : `${record.label}: fractional inches`);
      if (wheel && activeWheel?.record !== record) record.trigger.removeAttribute('aria-controls');
      else if (wheel) record.trigger.setAttribute('aria-controls', activeWheel.dialog.id);
      else record.trigger.setAttribute('aria-controls', record.menu.id);
      if (activeWheel?.record === record && (input.disabled || input.readOnly
        || !wheel || wheel.min !== activeWheel.bounds.min || wheel.max !== activeWheel.bounds.max
        || input.value !== activeWheel.originalValue)) closeSizeWheel();
      if (record.select && record.select.disabled !== Boolean(input.disabled || input.readOnly)) {
        record.select.disabled = Boolean(input.disabled || input.readOnly);
      }
      if (record.lastDecimal !== input.value
        && document.activeElement !== record.proxy && document.activeElement !== record.select) {
        hydrateFraction(record, input.value);
        record.lastDecimal = input.value;
      }
      if (record.trigger && record.trigger.disabled !== Boolean(input.disabled || input.readOnly)) {
        record.trigger.disabled = Boolean(input.disabled || input.readOnly);
        if (record.trigger.disabled && openFraction?.record === record) closeFractionMenu();
      }
      updateFractionOptions(state, record);
    });
  }

  function writeFraction(state, record, notifyChange = false) {
    const raw = record.proxy.value.trim();
    if (record.incompatibleDecimal && !raw) return false;
    const validParts = /^\d+$/.test(raw) && /^(?:[0-9]|1[0-5])$/.test(record.select.value);
    const value = validParts ? Number(raw) + Number(record.select.value) / 16 : NaN;
    const { min, max } = dimensionLimits(state, record.input);
    let message = '';
    if (raw && (!Number.isFinite(value) || value <= 0)) {
      message = 'Enter whole inches, then choose a fraction from the dropdown.';
    }
    else if (raw && ((Number.isFinite(min) && value < min) || (Number.isFinite(max) && value > max))) {
      message = Number.isFinite(min) && Number.isFinite(max)
        ? `Enter a size between ${min} and ${max} inches.`
        : Number.isFinite(min) ? `Enter at least ${min} inches.` : `Enter no more than ${max} inches.`;
    }
    record.proxy.setCustomValidity(message);
    record.proxy.setAttribute('aria-invalid', String(Boolean(message) || (!raw && record.proxy.required)));
    const decimal = raw && Number.isFinite(value) && value > 0 ? String(value) : '';
    record.lastDecimal = decimal;
    updateFractionOptions(state, record);
    if (record.input.value !== decimal) {
      // Use the native setter so framework value trackers see the change.
      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
      setter.call(record.input, decimal);
      record.input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    rememberConditionalSize(state, true);
    if (notifyChange) record.input.dispatchEvent(new Event('change', { bubbles: true }));
    return !message && decimal !== '';
  }

  function showIntegerNotice(record, message = 'Whole inches only. Choose the fraction on the right.', persistent = false) {
    if (!record.feedback) return;
    clearTimeout(record.noticeTimer);
    record.feedback.textContent = message;
    record.feedback.hidden = false;
    positionIntegerNotice(record);
    if (!persistent) record.noticeTimer = setTimeout(() => { record.feedback.hidden = true; }, 2500);
  }

  function acceptWhole(record) {
    const raw = record.proxy.value;
    if (!/^\d*$/.test(raw) || (record.proxy.maxLength > 0 && raw.length > record.proxy.maxLength)) {
      record.proxy.value = record.lastWhole || '';
      showIntegerNotice(record);
      return false;
    }
    record.lastWhole = raw;
    record.incompatibleDecimal = null;
    if (record.feedback) record.feedback.hidden = true;
    return true;
  }

  function buildFractionMenu(state, record, label) {
    const id = `inch-fraction-menu-${++menuSerial}`;
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'inch-split-trigger';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-label', `${label}: fractional inches`);
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-controls', id);
    trigger.setAttribute('aria-expanded', 'false');
    const value = document.createElement('span');
    value.className = 'inch-split-trigger-value';
    const unit = document.createElement('span');
    unit.className = 'inch-split-trigger-unit';
    unit.textContent = 'in';
    const arrow = document.createElement('span');
    arrow.className = 'inch-split-trigger-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    trigger.append(value, unit, arrow);
    record.fractionShell.append(trigger);

    const menu = document.createElement('div');
    menu.id = id;
    menu.className = 'inch-split-menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', `${label}: fractional inches`);
    menu.hidden = true;
    const grid = document.createElement('div');
    grid.className = 'inch-split-menu-grid';
    grid.setAttribute('role', 'presentation');
    const rows = Array.from(record.select.options).map((option, index) => {
      const row = document.createElement('div');
      row.id = `${id}-${index}`;
      row.className = 'inch-split-option';
      row.setAttribute('role', 'option');
      row.dataset.tick = option.value;
      const mark = document.createElement('span');
      mark.className = 'inch-split-option-mark';
      mark.textContent = '✓';
      mark.setAttribute('aria-hidden', 'true');
      const fraction = document.createElement('span');
      fraction.className = 'inch-split-option-value';
      fraction.textContent = fractionalInches(String(index / 16));
      const unit = document.createElement('span');
      unit.className = 'inch-split-option-unit';
      unit.textContent = 'in';
      row.append(mark, fraction, unit);
      grid.append(row);
      return row;
    });
    menu.append(grid);
    document.body.append(menu);
    const feedback = document.createElement('span');
    feedback.className = 'inch-split-feedback';
    feedback.setAttribute('role', 'status');
    feedback.setAttribute('aria-live', 'polite');
    feedback.hidden = true;
    document.body.append(feedback);
    Object.assign(record, { trigger, triggerValue: value, menu, menuRows: rows, feedback, activeTick: 0, noticeTimer: 0 });
  }

  function refreshFractionMenu(record) {
    if (!record.trigger) return;
    const tick = Number(record.select.value);
    const label = fractionalInches(String(tick / 16));
    if (record.triggerValue.textContent !== label) record.triggerValue.textContent = label;
    record.menuRows.forEach((row, index) => {
      const selected = index === tick;
      const disabled = record.select.options[index].disabled;
      const selectedText = String(selected);
      const disabledText = String(disabled);
      if (row.getAttribute('aria-selected') !== selectedText) row.setAttribute('aria-selected', selectedText);
      if (row.getAttribute('aria-disabled') !== disabledText) row.setAttribute('aria-disabled', disabledText);
      row.dataset.active = String(index === record.activeTick);
    });
    if (openFraction?.record === record) {
      record.trigger.setAttribute('aria-activedescendant', record.menuRows[record.activeTick].id);
    }
  }

  function positionIntegerNotice(record) {
    if (!record.feedback || record.feedback.hidden) return;
    const anchor = record.root.getBoundingClientRect();
    const viewport = window.visualViewport;
    const leftEdge = (viewport?.offsetLeft || 0) + 8;
    const rightEdge = (viewport?.offsetLeft || 0) + (viewport?.width || window.innerWidth) - 8;
    const width = Math.min(Math.max(anchor.width, 200), rightEdge - leftEdge);
    record.feedback.style.width = `${width}px`;
    record.feedback.style.left = `${Math.max(leftEdge, Math.min(anchor.left, rightEdge - width)) + window.scrollX}px`;
    record.feedback.style.top = `${anchor.bottom + window.scrollY + 7}px`;
  }

  function positionFloatingControls(event) {
    if (event?.target && activeWheel?.dialog.contains(event.target)) return;
    positionFractionMenu();
    positionSizeWheel();
    states.forEach(state => state.fractionFields.forEach(positionIntegerNotice));
  }

  function positionFractionMenu() {
    if (!openFraction) return;
    const { record } = openFraction;
    const anchor = record.root.getBoundingClientRect();
    const viewport = window.visualViewport;
    const leftEdge = (viewport?.offsetLeft || 0) + 8;
    const rightEdge = (viewport?.offsetLeft || 0) + (viewport?.width || window.innerWidth) - 8;
    const width = Math.min(anchor.width, rightEdge - leftEdge);
    const left = Math.max(leftEdge, Math.min(anchor.left, rightEdge - width));
    // Match the whole Size field's edges, without changing the field's width.
    record.menu.dataset.compact = String(width < 220);
    record.menu.style.width = `${width}px`;
    record.menu.style.left = `${left + window.scrollX}px`;
    record.menu.style.top = `${anchor.bottom + window.scrollY + 7}px`;
  }

  function closeFractionMenu(restoreFocus = false) {
    if (!openFraction) return;
    const { record } = openFraction;
    record.menu.hidden = true;
    record.trigger.setAttribute('aria-expanded', 'false');
    record.trigger.removeAttribute('aria-activedescendant');
    openFraction = null;
    if (restoreFocus) record.trigger.focus({ preventScroll: true });
  }

  function openFractionMenu(state, record, keyboard = false) {
    if (record.trigger.disabled) return;
    if (wheelBounds(state, record)) { openSizeWheel(state, record, record.trigger, keyboard); return; }
    closeFractionMenu();
    updateFractionOptions(state, record);
    const selected = Number(record.select.value);
    record.activeTick = record.select.options[selected]?.disabled
      ? Array.from(record.select.options).findIndex(option => !option.disabled)
      : selected;
    if (record.activeTick < 0) record.activeTick = selected;
    openFraction = { state, record };
    record.menu.hidden = false;
    record.trigger.setAttribute('aria-expanded', 'true');
    refreshFractionMenu(record);
    positionFractionMenu();
    record.trigger.focus({ preventScroll: true });
  }

  function chooseFraction(state, record, tick) {
    if (!record.select.options[tick] || record.select.options[tick].disabled) return;
    record.select.value = String(tick);
    writeFraction(state, record, true);
    refreshFractionMenu(record);
    closeFractionMenu(true);
  }

  // Work in sixteenths throughout, including fractional range boundaries.
  // Unreadable or exceptionally large ranges keep the existing manual editor.
  function wheelBounds(state, record) {
    if (!pickerText?.enabled || typeof window.HTMLDialogElement?.prototype.show !== 'function') return null;
    const limits = dimensionLimits(state, record.input);
    if (!Number.isFinite(limits.min) || !Number.isFinite(limits.max)) return null;
    const min = Math.max(1, Math.ceil(limits.min * 16 - 1e-9));
    const max = Math.floor(limits.max * 16 + 1e-9);
    return min <= max && Math.floor(max / 16) - Math.floor(min / 16) <= 1000 ? { min, max } : null;
  }

  function stopColumnWheel(column, finish = false) {
    cancelAnimationFrame(column.wheelFrame);
    column.wheelFrame = 0;
    if (finish && column.motion) column.scroller.scrollTop = column.motion.continuous
      ? Math.round(column.motion.target / wheelRowHeight) * wheelRowHeight : column.motion.target;
    column.motion = null;
    column.scroller.style.scrollSnapType = '';
  }

  function resetColumnGesture(column) {
    column.precisionWheel = false;
    column.wheelAt = -Infinity;
  }

  function closeSizeWheel(restoreFocus = true) {
    const wheel = activeWheel;
    if (!wheel) return;
    if (wheel.userDirty) wheel.flush?.();
    activeWheel = null;
    clearTimeout(wheel.leaveTimer);
    for (const column of [wheel.whole, wheel.fraction]) {
      clearTimeout(column.timer);
      stopColumnWheel(column);
      resetColumnGesture(column);
    }
    cancelAnimationFrame(wheel.paintFrame);
    if (wheel.dialog.open) wheel.dialog.close();
    wheel.dialog.remove();
    wheel.record.trigger.setAttribute('aria-expanded', 'false');
    wheel.record.proxy.setAttribute('aria-expanded', 'false');
    wheel.record.trigger.removeAttribute('aria-controls');
    wheel.record.proxy.removeAttribute('aria-controls');
    if (restoreFocus && wheel.opener.isConnected) wheel.opener.focus({ preventScroll: true });
  }

  function positionSizeWheel() {
    const wheel = activeWheel;
    if (!wheel) return;
    const anchor = wheel.record.root.getBoundingClientRect();
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft || 0;
    const top = viewport?.offsetTop || 0;
    const width = viewport?.width || window.innerWidth;
    const height = viewport?.height || window.innerHeight;
    if (anchor.bottom < top || anchor.top > top + height) { closeSizeWheel(false); return; }
    const panelWidth = Math.min(anchor.width, width - 16);
    const below = anchor.bottom + 8;
    const belowSpace = Math.max(0, top + height - below - 8);
    const aboveSpace = Math.max(0, anchor.top - top - 16);
    // Keep the touch panel next to its field, using the space above only when
    // there is not enough room for three rows below.
    const above = window.innerWidth <= 749 && belowSpace < 3 * wheelRowHeight + 18 && aboveSpace > belowSpace;
    const available = above ? aboveSpace : belowSpace;
    const rows = available >= 7 * wheelRowHeight + 18 ? 7
      : available >= 5 * wheelRowHeight + 18 ? 5 : 3;
    const panelHeight = Math.min(available, rows * wheelRowHeight + 18);
    wheel.dialog.dataset.placement = above ? 'above' : 'below';
    wheel.dialog.style.width = `${panelWidth}px`;
    wheel.dialog.style.left = `${Math.max(left + 8, Math.min(anchor.left, left + width - panelWidth - 8))}px`;
    wheel.dialog.style.top = `${above ? anchor.top - 8 - panelHeight : below}px`;
    wheel.dialog.style.maxHeight = `${available}px`;
    wheel.dialog.style.setProperty('--inch-wheel-rows', String(rows));
  }

  function openSizeWheel(state, record, opener, keyboard = false) {
    const bounds = wheelBounds(state, record);
    if (!bounds || record.input.disabled || record.input.readOnly) return;
    if (activeWheel?.record === record) return;
    closeSizeWheel(false);
    closeFractionMenu();
    const create = (tag, className, text) => {
      const node = document.createElement(tag);
      node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const dialog = create('dialog', 'inch-wheel-dialog');
    dialog.dataset.keyboard = String(keyboard);
    dialog.dataset.mode = 'anchored';
    dialog.style.setProperty('--inch-wheel-row-height', `${wheelRowHeight}px`);
    dialog.setAttribute('aria-modal', 'false');
    dialog.setAttribute('aria-label', record.label);
    const id = `inch-wheel-${++menuSerial}`;
    dialog.id = id;
    const columns = create('div', 'inch-wheel-columns');
    const preview = create('p', 'inch-wheel-status');
    preview.setAttribute('role', 'status');
    preview.setAttribute('aria-live', 'polite');
    dialog.append(columns, preview);
    const current = Number(record.input.value);
    const initial = record.input.value && Number.isFinite(current) ? Math.round(current * 16) : bounds.min;
    const draft = Math.max(bounds.min, Math.min(bounds.max, initial));
    const wheel = {
      state, record, opener, dialog, bounds, draft, paintFrame: 0, userDirty: false, leaveTimer: 0, originalValue: record.input.value
    };
    // macOS already supplies continuous, accelerated trackpad / Magic Mouse scrolling.
    // Keep its original native path, with no blocking wheel listener or extra animation.
    const platform = window.navigator?.userAgentData?.platform || window.navigator?.platform || window.navigator?.userAgent || '';
    const nativeWheel = /Mac|iPhone|iPad|iPod/i.test(platform);
    const windowsWheel = /Win/i.test(platform);
    const rowHeight = wheelRowHeight; // Also sets the stylesheet's snap row height.
    const modulo = (value, length) => ((value % length) + length) % length;
    const selectedIndex = column => modulo(Math.round(column.scroller.scrollTop / rowHeight), column.values.length);
    const selectedValue = column => column.values[selectedIndex(column)];
    const updatePreview = () => { preview.textContent = `${fractionalInches(String(wheel.draft / 16))} in`; };
    const paint = () => {
      wheel.paintFrame = 0;
      if (activeWheel !== wheel) return;
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      for (const column of [wheel.whole, wheel.fraction]) {
        const center = column.scroller.scrollTop / rowHeight;
        const visible = new Set();
        for (let index = Math.max(0, Math.floor(center) - 3); index <= Math.min(column.rows.length - 1, Math.ceil(center) + 3); index++) {
          const distance = index - center;
          const amount = Math.min(3, Math.abs(distance));
          const label = column.rows[index].children[0];
          label.style.transform = reduced ? 'none'
            : `perspective(320px) rotateX(${-distance * 16}deg) scale(${Math.max(.76, 1.12 - amount * .12)})`;
          label.style.opacity = String(Math.max(.28, 1 - amount * .24));
          visible.add(index);
        }
        column.painted.forEach(index => {
          if (!visible.has(index)) {
            column.rows[index].children[0].style.opacity = '0';
            column.rows[index].children[0].style.transform = 'none';
          }
        });
        column.painted = visible;
      }
    };
    const queuePaint = () => { if (!wheel.paintFrame) wheel.paintFrame = requestAnimationFrame(paint); };
    const recenter = column => {
      if (column.copies === 1) return;
      const position = column.scroller.scrollTop / rowHeight;
      const length = column.values.length;
      // Reset only in the outer cycles. Equivalent neighboring labels and the
      // fractional scroll offset are preserved, making the seam invisible.
      if (position < length || position >= (column.copies - 1) * length) {
        column.scroller.scrollTop = (column.middle + modulo(position, length)) * rowHeight;
      }
    };
    const mark = column => {
      const index = column.values.indexOf(column.value);
      column.rows.forEach((row, rowIndex) => {
        // Only the canonical middle cycle is exposed to assistive technology.
        if (rowIndex >= column.middle && rowIndex < column.middle + column.values.length) {
          row.setAttribute('aria-selected', String(rowIndex === column.middle + index));
        }
      });
      column.scroller.setAttribute('aria-activedescendant', column.rows[column.middle + index].id);
      queuePaint();
    };
    const render = (column, values, value) => {
      clearTimeout(column.timer);
      stopColumnWheel(column);
      resetColumnGesture(column);
      column.values = values;
      column.value = value;
      column.copies = values.length > 1 ? 5 : 1;
      column.middle = Math.floor(column.copies / 2) * values.length;
      column.painted = new Set();
      column.scroller.replaceChildren();
      column.rows = Array.from({ length: values.length * column.copies }, (_, index) => {
        const item = values[index % values.length];
        const text = String(column.kind === 'whole' ? item : fractionalInches(String(item / 16)));
        const row = create('div', 'inch-wheel-row');
        row.append(create('span', 'inch-wheel-row-label', text));
        row.id = `${id}-${column.kind}-${index}`;
        if (index >= column.middle && index < column.middle + values.length) row.setAttribute('role', 'option');
        else { row.setAttribute('role', 'presentation'); row.setAttribute('aria-hidden', 'true'); }
        row.addEventListener('click', () => {
          stopColumnWheel(column);
          resetColumnGesture(column);
          wheel.userDirty = true;
          column.scroller.scrollTop = index * rowHeight;
          recenter(column);
          settle(column);
        });
        column.scroller.append(row);
        return row;
      });
      column.restingTop = (column.middle + values.indexOf(value)) * rowHeight;
      column.scroller.scrollTop = column.restingTop;
      mark(column);
    };
    const fractions = whole => {
      const start = Math.max(0, bounds.min - whole * 16);
      const end = Math.min(15, bounds.max - whole * 16);
      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    };
    const settle = (column, autoWrite = true) => {
      if (activeWheel !== wheel) return;
      clearTimeout(column.timer);
      recenter(column);
      const value = selectedValue(column);
      if (column.kind === 'whole') {
        const wanted = selectedValue(wheel.fraction);
        column.value = value;
        mark(column);
        const allowed = fractions(value);
        const tick = Math.max(allowed[0], Math.min(allowed[allowed.length - 1], wanted));
        if (allowed.join(',') !== wheel.fraction.values.join(',')) render(wheel.fraction, allowed, tick);
        else { wheel.fraction.value = tick; mark(wheel.fraction); }
        wheel.draft = value * 16 + tick;
      } else {
        column.value = value;
        mark(column);
        wheel.draft = wheel.whole.value * 16 + value;
      }
      column.restingTop = column.scroller.scrollTop;
      updatePreview();
      if (autoWrite && wheel.userDirty) wheel.flush();
    };
    wheel.flush = () => {
      if (!wheel.userDirty) return;
      // Commit the requested row even if the pointer leaves during its short animation.
      for (const column of [wheel.whole, wheel.fraction]) stopColumnWheel(column, true);
      state.limits = configuredBounds(state.option, inchFields(state.option));
      const latest = wheelBounds(state, record);
      if (!record.input.isConnected || record.input.disabled || record.input.readOnly || !latest
        || latest.min !== bounds.min || latest.max !== bounds.max || record.input.value !== wheel.originalValue) {
        wheel.userDirty = false;
        queueSync();
        return;
      }
      // Read both wheels together before publishing one decimal to Easify.
      settle(wheel.whole, false);
      settle(wheel.fraction, false);
      record.proxy.value = String(Math.floor(wheel.draft / 16));
      record.lastWhole = record.proxy.value;
      record.incompatibleDecimal = null;
      record.select.value = String(wheel.draft % 16);
      record.feedback.hidden = true;
      const decimal = String(wheel.draft / 16);
      wheel.userDirty = false;
      wheel.originalValue = decimal;
      if (record.input.value !== decimal) writeFraction(state, record, true);
    };
    wheel.cancelLeave = () => { clearTimeout(wheel.leaveTimer); wheel.leaveTimer = 0; };
    wheel.scheduleLeave = () => {
      if (wheel.leaveTimer) return;
      wheel.leaveTimer = setTimeout(() => {
        if (activeWheel === wheel) closeSizeWheel(false);
      }, 160);
    };
    const moveWithWheel = (column, event) => {
      clearTimeout(column.timer);
      const rows = Number(dialog.style.getPropertyValue('--inch-wheel-rows')) || 7;
      // Decouple driver units from the 28px visual rows. Accumulate distance, not event count.
      const distance = event.deltaMode === 1 ? event.deltaY / 3
        : event.deltaMode === 2 ? event.deltaY * rows : event.deltaY / wheelPixelsPerRow;
      let motion = column.motion;
      if (!motion?.continuous) {
        stopColumnWheel(column);
        column.scroller.style.scrollSnapType = 'none';
        const position = column.scroller.scrollTop;
        motion = column.motion = { continuous: true, position, target: position, lastFrame: null, lastInput: null, snapping: false };
      }
      motion.target += distance * rowHeight;
      motion.lastInput = null;
      motion.snapping = false;
      wheel.userDirty = true;
      if (column.wheelFrame) return;
      const animate = timestamp => {
        if (activeWheel !== wheel || column.motion !== motion) return;
        motion.lastInput ??= timestamp;
        const elapsed = Math.min(32, timestamp - (motion.lastFrame ?? timestamp - 16));
        motion.lastFrame = timestamp;
        // Keep all input while moving. Snap only after the input stream becomes quiet.
        if (timestamp - motion.lastInput >= 100 && !motion.snapping) {
          motion.target = Math.round(motion.target / rowHeight) * rowHeight;
          motion.snapping = true;
        }
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        motion.position = reduced ? motion.target
          : motion.position + (motion.target - motion.position) * (1 - Math.exp(-elapsed / 50));
        // Rebase both coordinates together, preserving velocity and the fractional offset at a seam.
        const position = motion.position / rowHeight;
        const length = column.values.length;
        if (position < length || position >= (column.copies - 1) * length) {
          const shift = (column.middle + modulo(position, length) - position) * rowHeight;
          motion.position += shift;
          motion.target += shift;
        }
        column.scroller.scrollTop = motion.position;
        queuePaint();
        if (motion.snapping && Math.abs(motion.target - motion.position) < .1) {
          stopColumnWheel(column, true);
          settle(column);
        } else column.wheelFrame = requestAnimationFrame(animate);
      };
      column.wheelFrame = requestAnimationFrame(animate);
    };
    const stepWithWheel = (column, direction) => {
      clearTimeout(column.timer);
      const previous = column.motion?.target ?? Math.round(column.scroller.scrollTop / rowHeight) * rowHeight;
      const base = column.middle + modulo(previous / rowHeight, column.values.length);
      const from = column.scroller.scrollTop + base * rowHeight - previous;
      const target = (base + direction) * rowHeight;
      stopColumnWheel(column);
      wheel.userDirty = true;
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        column.scroller.scrollTop = target;
        settle(column);
        return;
      }
      // Own mouse-wheel movement so native momentum and mandatory snap cannot add extra rows.
      // Touch dragging still uses native scrolling below.
      column.scroller.style.scrollSnapType = 'none';
      column.scroller.scrollTop = from;
      column.motion = { target, start: null, position: from };
      const animate = timestamp => {
        if (activeWheel !== wheel || !column.motion) return;
        column.motion.start ??= timestamp;
        const progress = Math.min(1, (timestamp - column.motion.start) / 110);
        column.motion.position = from + (target - from) * (1 - (1 - progress) ** 3);
        column.scroller.scrollTop = column.motion.position;
        queuePaint();
        if (progress < 1) column.wheelFrame = requestAnimationFrame(animate);
        else {
          stopColumnWheel(column, true);
          settle(column);
        }
      };
      column.wheelFrame = requestAnimationFrame(animate);
    };
    const buildColumn = (kind, label) => {
      const wrapper = create('div', 'inch-wheel-column');
      const caption = create('p', 'inch-wheel-caption', label);
      caption.id = `${id}-${kind}-label`;
      const scroller = create('div', 'inch-wheel-scroller');
      scroller.setAttribute('role', 'listbox');
      scroller.setAttribute('tabindex', '0');
      scroller.setAttribute('aria-labelledby', caption.id);
      scroller.setAttribute('aria-orientation', 'vertical');
      const column = { kind, scroller, values: [], rows: [], timer: 0, copies: 1, middle: 0, painted: new Set(), restingTop: 0,
        motion: null, wheelFrame: 0, precisionWheel: false, wheelAt: -Infinity };
      if (!nativeWheel) scroller.addEventListener('wheel', event => {
        const delta = event.deltaY;
        if (activeWheel !== wheel || event.ctrlKey || !Number.isFinite(delta) || !delta
          || Math.abs(event.deltaX || 0) > Math.abs(delta)) return;
        wheel.cancelLeave();
        if (column.values.length < 2) { event.preventDefault(); return; }
        if (windowsWheel) {
          event.preventDefault();
          moveWithWheel(column, event);
          return;
        }
        if (event.timeStamp - column.wheelAt > 180) column.precisionWheel = false;
        column.wheelAt = event.timeStamp;
        // Once a pixel gesture reveals precision input, keep its whole momentum stream native,
        // including later large deltas. Only coarse mouse signals use the single-row correction.
        if (!event.deltaMode && (column.precisionWheel || !Number.isInteger(delta) || Math.abs(delta) < rowHeight)) {
          column.precisionWheel = true;
          stopColumnWheel(column);
          return;
        }
        event.preventDefault();
        column.precisionWheel = false;
        stepWithWheel(column, Math.sign(delta));
      }, { passive: false });
      scroller.addEventListener('pointerdown', () => {
        stopColumnWheel(column);
        resetColumnGesture(column);
      }, { passive: true });
      scroller.addEventListener('scroll', () => {
        if (column.motion) {
          if (windowsWheel && Math.abs(scroller.scrollTop - column.motion.position) > .5) scroller.scrollTop = column.motion.position;
          queuePaint();
          return;
        }
        if (Math.abs(scroller.scrollTop - column.restingTop) > .5) wheel.userDirty = true;
        recenter(column);
        queuePaint();
        clearTimeout(column.timer);
        column.timer = setTimeout(() => settle(column), 140);
      }, { passive: true });
      scroller.addEventListener('scrollend', () => { if (!column.motion) settle(column); });
      scroller.addEventListener('keydown', event => {
        const delta = { ArrowDown: 1, ArrowUp: -1, PageDown: 5, PageUp: -5 }[event.key];
        if (delta === undefined && event.key !== 'Home' && event.key !== 'End') return;
        event.preventDefault();
        stopColumnWheel(column, true);
        resetColumnGesture(column);
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? column.values.length - 1
          : modulo(selectedIndex(column) + delta, column.values.length);
        wheel.userDirty = true;
        scroller.scrollTop = (column.middle + index) * rowHeight;
        settle(column);
      });
      wrapper.append(caption, scroller);
      columns.append(wrapper);
      return column;
    };
    wheel.whole = buildColumn('whole', pickerText.whole);
    wheel.fraction = buildColumn('fraction', pickerText.fraction);
    activeWheel = wheel;
    const minimumWhole = Math.floor(bounds.min / 16);
    const maximumWhole = Math.floor(bounds.max / 16);
    const whole = Math.floor(draft / 16);
    document.body.append(dialog);
    // Both pointer and touch use the same non-modal, automatically saved panel.
    dialog.addEventListener('cancel', event => { event.preventDefault(); closeSizeWheel(); });
    // A touch-opened wheel should not get a large keyboard-focus frame. Restore
    // the visible focus indicator as soon as the user navigates with a keyboard.
    dialog.addEventListener('keydown', () => { dialog.dataset.keyboard = 'true'; }, true);
    dialog.addEventListener('pointerdown', () => { dialog.dataset.keyboard = 'false'; }, true);
    dialog.addEventListener('close', () => { if (activeWheel === wheel) closeSizeWheel(); });
    dialog.addEventListener('click', event => {
      const rect = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right
        || event.clientY < rect.top || event.clientY > rect.bottom)) closeSizeWheel();
    });
    dialog.addEventListener('pointerenter', wheel.cancelLeave);
    dialog.addEventListener('pointerleave', event => {
      if (event.pointerType === 'touch' || (event.relatedTarget && record.root.contains(event.relatedTarget))) return;
      wheel.scheduleLeave();
    });
    try { dialog.show(); }
    catch { closeSizeWheel(); return; }
    positionSizeWheel();
    if (activeWheel !== wheel) return;
    render(wheel.whole, Array.from({ length: maximumWhole - minimumWhole + 1 }, (_, index) => minimumWhole + index), whole);
    render(wheel.fraction, fractions(whole), draft % 16);
    updatePreview();
    cancelAnimationFrame(wheel.paintFrame);
    paint();
    record.proxy.setAttribute('aria-expanded', 'true');
    record.trigger.setAttribute('aria-expanded', 'true');
    record.proxy.setAttribute('aria-controls', id);
    record.trigger.setAttribute('aria-controls', id);
    wheel.whole.scroller.focus({ preventScroll: true });
  }

  function sync() {
    pendingFrame = 0;
    const nextObserved = new Set();
    const candidates = new Map();
    document.querySelectorAll('.po-option-root').forEach(option => {
      const fields = inchFields(option);
      if (fields.length && option.closest('.po-options-root')) candidates.set(option, fields);
    });
    states.forEach((state, option) => {
      if (candidates.has(option)) return;
      state.fractionFields.forEach(restoreFraction);
      states.delete(option);
    });
    candidates.forEach((fields, option) => {
      let state = states.get(option);
      if (!state) {
        state = { option, inputs: [], limits: { min: [], max: [] }, fractionFields: new Map() };
        states.set(option, state);
      }
      state.inputs = fields.map(field => field.querySelector('input'));
      state.limits = configuredBounds(option, fields);
      fields.forEach(field => nextObserved.add(field));
    });
    syncConditionalSizes();
    states.forEach(syncFractionFields);
    observed.forEach(element => {
      if (!nextObserved.has(element)) resizeObserver?.unobserve(element);
    });
    nextObserved.forEach(element => {
      if (!observed.has(element)) resizeObserver?.observe(element);
    });
    observed = nextObserved;
    positionFloatingControls();
  }

  function start() {
    // Keep discovery for late app mounts and section replacement, but ignore
    // unrelated cart, gallery, recommendation and portal DOM updates.
    const optionsSelector = '.po-options-root, .po-option-root, [data-option-set-id]';
    const containsOptions = node => node.nodeType === 1
      && (node.matches(optionsSelector) || node.querySelector(optionsSelector));
    new MutationObserver(mutations => {
      const relevant = mutations.some(mutation => {
        if (mutation.target.closest?.(optionsSelector)) return true;
        if (mutation.type !== 'childList') return false;
        return Array.from(mutation.addedNodes).some(containsOptions)
          || Array.from(mutation.removedNodes).some(containsOptions);
      });
      if (relevant) queueSync();
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-invalid', 'min', 'max', 'disabled', 'readonly', 'required', 'data-option-id', 'data-option-set-id']
    });
    document.addEventListener('beforeinput', event => {
      const found = fractionRecord(event.target);
      if (!found || event.target !== found.record.proxy || event.isComposing) return;
      if (!event.inputType?.startsWith('insert') || event.data === null || event.data === undefined) return;
      const { record } = found;
      const input = record.proxy;
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? start;
      const proposed = input.value.slice(0, start) + event.data + input.value.slice(end);
      if (!/^\d*$/.test(proposed) || (input.maxLength > 0 && proposed.length > input.maxLength)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showIntegerNotice(record);
      }
    }, true);
    document.addEventListener('paste', event => {
      const found = fractionRecord(event.target);
      if (!found || event.target !== found.record.proxy) return;
      const text = event.clipboardData?.getData('text/plain');
      if (text !== undefined && !/^\d*$/.test(text)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showIntegerNotice(found.record, 'Paste whole inches only. Choose the fraction on the right.');
      }
    }, true);
    document.addEventListener('compositionend', event => {
      const found = fractionRecord(event.target);
      if (!found || event.target !== found.record.proxy) return;
      event.stopImmediatePropagation();
      acceptWhole(found.record);
      writeFraction(found.state, found.record);
    }, true);
    ['input', 'change', 'focusout'].forEach(type => {
      document.addEventListener(type, event => {
        const found = fractionRecord(event.target);
        if (!found) {
          if (type !== 'focusout') states.forEach(state => {
            if (state.inputs.includes(event.target)) rememberConditionalSize(state, Boolean(event.isTrusted));
          });
          return;
        }
        // Easify must receive decimal events from its own original input only.
        event.stopImmediatePropagation();
        const { state, record } = found;
        if (record.wheelEnabled) return;
        if (event.isComposing && type === 'input') return;
        if (event.target === record.proxy
          && !(record.incompatibleDecimal && !record.proxy.value && type === 'focusout')) acceptWhole(record);
        const valid = writeFraction(state, record, type !== 'input');
        if (type === 'focusout' && valid) {
          record.proxy.value = String(Number(record.proxy.value));
          record.lastWhole = record.proxy.value;
        }
      }, true);
    });
    document.addEventListener('click', event => {
      const whole = fractionRecord(event.target);
      if (whole && event.target === whole.record.proxy && whole.record.wheelEnabled) {
        event.preventDefault();
        event.stopPropagation();
        openSizeWheel(whole.state, whole.record, whole.record.proxy);
        return;
      }
      const trigger = event.target.closest?.('.inch-split-trigger');
      const row = event.target.closest?.('.inch-split-option');
      if (trigger || row) {
        for (const state of states.values()) {
          for (const record of state.fractionFields.values()) {
            if (record.trigger === trigger || (row && record.menu?.contains(row))) {
              event.preventDefault();
              event.stopPropagation();
              if (row) chooseFraction(state, record, Number(row.dataset.tick));
              else if (openFraction?.record === record) closeFractionMenu();
              else openFractionMenu(state, record);
              return;
            }
          }
        }
      }
    }, true);
    document.addEventListener('focusin', event => {
      if (activeWheel && !pointerActive
        && !activeWheel.dialog.contains(event.target) && !activeWheel.record.root.contains(event.target)) closeSizeWheel(false);
      if (openFraction && !pointerActive && event.target !== openFraction.record.trigger
        && !openFraction.record.menu.contains(event.target)) closeFractionMenu();
    });
    document.addEventListener('pointermove', event => {
      if (!activeWheel || event.pointerType === 'touch') return;
      if (activeWheel.dialog.contains(event.target) || activeWheel.record.root.contains(event.target)) activeWheel.cancelLeave();
      else activeWheel.scheduleLeave();
    }, { passive: true });
    document.addEventListener('pointerdown', event => {
      pointerActive = true;
      if (activeWheel && !activeWheel.dialog.contains(event.target)
        && !activeWheel.record.root.contains(event.target)) closeSizeWheel(false);
      if (openFraction && !openFraction.record.fractionShell.contains(event.target)
        && !openFraction.record.menu.contains(event.target)) closeFractionMenu();
    }, true);
    document.addEventListener('keydown', event => {
      if (activeWheel && event.key === 'Escape') { event.preventDefault(); closeSizeWheel(); return; }
      const found = fractionRecord(event.target);
      if (found?.record.wheelEnabled && event.target === found.record.proxy
        && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        openSizeWheel(found.state, found.record, found.record.proxy, true);
        return;
      }
      if (found && event.target === found.record.proxy
        && !event.isComposing && !event.ctrlKey && !event.metaKey && !event.altKey
        && event.key?.length === 1 && !/^\d$/.test(event.key)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showIntegerNotice(found.record);
        return;
      }
      let owner;
      for (const state of states.values()) {
        for (const record of state.fractionFields.values()) {
          if (record.trigger === event.target) owner = { state, record };
        }
      }
      if (!owner) return;
      const { state, record } = owner;
      const opened = openFraction?.record === record;
      if (event.key === 'Escape' && opened) {
        event.preventDefault(); closeFractionMenu(true); return;
      }
      if (event.key === 'Tab') { closeFractionMenu(); return; }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (opened) chooseFraction(state, record, record.activeTick);
        else openFractionMenu(state, record, true);
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (!opened) { openFractionMenu(state, record, true); return; }
      const enabled = Array.from(record.select.options).map((option, index) => option.disabled ? -1 : index).filter(index => index >= 0);
      if (!enabled.length) return;
      const current = Math.max(0, enabled.indexOf(record.activeTick));
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1
        : (current + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length;
      record.activeTick = enabled[next];
      refreshFractionMenu(record);
      record.menuRows[record.activeTick].scrollIntoView({ block: 'nearest' });
    }, true);
    const finishPointer = () => { pointerActive = false; };
    document.addEventListener('pointerup', finishPointer, true);
    document.addEventListener('pointercancel', finishPointer, true);
    window.addEventListener('blur', finishPointer);
    window.addEventListener('resize', queueSync, { passive: true });
    window.addEventListener('scroll', positionFloatingControls, { passive: true, capture: true });
    window.visualViewport?.addEventListener('resize', positionFloatingControls, { passive: true });
    window.visualViewport?.addEventListener('scroll', positionFloatingControls, { passive: true });
    document.fonts?.ready.then(queueSync);
    queueSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
